import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MobileLayout from '../MobileLayout';
import { apiGet, apiPostFormData } from '../api';

interface Shot { id: string; file: File; previewUrl: string; }

interface ProjectFull {
  id: string; title: string; customerId: string; customer?: { id: string; name: string };
}

const MobileCapture: React.FC = () => {
  const { projectId = '' } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<ProjectFull | null>(null);
  const [shots, setShots] = useState<Shot[]>([]);
  const [docName, setDocName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const libraryInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    apiGet<ProjectFull>(`/projects/${projectId}`).then(setProject).catch((e) =>
      setError(e instanceof Error ? e.message : 'Failed to load project')
    );
  }, [projectId]);

  useEffect(() => () => {
    // Revoke any leftover object URLs on unmount.
    shots.forEach((s) => URL.revokeObjectURL(s.previewUrl));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const next: Shot[] = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      if (!f.type.startsWith('image/') && !/\.(jpe?g|png|webp|heic|heif)$/i.test(f.name)) continue;
      next.push({
        id: `${Date.now()}-${i}-${Math.random().toString(36).slice(2, 8)}`,
        file: f,
        previewUrl: URL.createObjectURL(f),
      });
    }
    setShots((s) => [...s, ...next].slice(0, 20));
  };

  const removeShot = (id: string) => {
    setShots((s) => {
      const target = s.find((x) => x.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return s.filter((x) => x.id !== id);
    });
  };

  const upload = async () => {
    if (!project || shots.length === 0) return;
    setUploading(true);
    setError(null);
    setProgress(0);
    try {
      const form = new FormData();
      form.append('projectId', project.id);
      form.append('customerId', project.customerId);
      if (docName.trim()) form.append('name', docName.trim());
      shots.forEach((s, idx) => form.append('photos', s.file, s.file.name || `photo-${idx + 1}.jpg`));
      await apiPostFormData('/documents/photos', form, setProgress);
      // Clean up URLs and head back to project.
      shots.forEach((s) => URL.revokeObjectURL(s.previewUrl));
      navigate(`/m/projects/${project.id}`, { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
      setUploading(false);
    }
  };

  return (
    <MobileLayout title="Add Photos" back hideNav>
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => addFiles(e.target.files)}
      />
      <input
        ref={libraryInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => addFiles(e.target.files)}
      />

      <div className="px-3 pt-3">
        <div className="rounded-xl bg-white p-3 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700">
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Document name (optional)
          </label>
          <input
            type="text"
            value={docName}
            onChange={(e) => setDocName(e.target.value)}
            placeholder="e.g. Roof damage 5/24"
            className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
          />
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3 px-3">
        <button
          type="button"
          onClick={() => cameraInputRef.current?.click()}
          className="flex items-center justify-center gap-2 rounded-xl bg-primary px-3 py-3 text-sm font-semibold text-white shadow-sm active:opacity-90"
          style={{ backgroundColor: '#1993e5' }}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5"><path d="M9 2 7.2 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-3.2L15 2H9zm3 15a5 5 0 1 1 0-10 5 5 0 0 1 0 10z" /></svg>
          Take Photo
        </button>
        <button
          type="button"
          onClick={() => libraryInputRef.current?.click()}
          className="flex items-center justify-center gap-2 rounded-xl bg-white px-3 py-3 text-sm font-semibold text-slate-900 shadow-sm ring-1 ring-slate-200 active:bg-slate-50 dark:bg-slate-900 dark:text-white dark:ring-slate-700"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5" style={{ color: '#1993e5' }}><path d="M21 19V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2zM8.5 13.5l2.5 3 3.5-4.5 4.5 6H5l3.5-4.5z" /></svg>
          Choose Photos
        </button>
      </div>

      <div className="mt-4 px-3">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {shots.length === 0 ? 'No photos yet' : `${shots.length} ${shots.length === 1 ? 'photo' : 'photos'} (will become 1 PDF)`}
        </h2>
      </div>

      <div className="grid grid-cols-3 gap-2 px-3 pt-2">
        {shots.map((s, idx) => (
          <div key={s.id} className="relative aspect-square overflow-hidden rounded-lg bg-slate-100 dark:bg-slate-800">
            <img src={s.previewUrl} alt={`Page ${idx + 1}`} className="h-full w-full object-cover" />
            <span className="absolute left-1 top-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
              {idx + 1}
            </span>
            <button
              type="button"
              onClick={() => removeShot(s.id)}
              aria-label="Remove photo"
              className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white"
            >
              ×
            </button>
          </div>
        ))}
      </div>

      {error && (
        <div className="mx-3 mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/30 dark:text-red-200">
          {error}
        </div>
      )}

      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white px-3 py-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] dark:border-slate-700 dark:bg-slate-900">
        {uploading ? (
          <div className="flex flex-col gap-2">
            <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
              <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${progress}%`, backgroundColor: '#1993e5' }} />
            </div>
            <div className="text-center text-xs text-slate-500">Uploading… {progress}%</div>
          </div>
        ) : (
          <button
            type="button"
            disabled={shots.length === 0}
            onClick={upload}
            className="w-full rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
            style={{ backgroundColor: '#1993e5' }}
          >
            {shots.length === 0 ? 'Add a photo to continue' : `Upload ${shots.length} ${shots.length === 1 ? 'photo' : 'photos'}`}
          </button>
        )}
      </div>
    </MobileLayout>
  );
};

export default MobileCapture;
